import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { useGetInfluencerFacets } from "@entities/influencer/lib/hooks/useGetFacets";
import { useGetLocationFacets } from "@entities/influencer/lib/hooks/useGetLocationFacets";
import { SocialMediaForm } from "@entities/influencer/ui/SocialMedia/SocialMediaForm";
import {
  GenderEnum,
  ICreateInfluencerRequest,
  IInfluencerRequestAdd,
  SocialMedia,
} from "@shared/lib/types/influencer";
import { formatData } from "@shared/lib/utils/formatData";
import { getPlatformIcon } from "@shared/lib/utils/getPlatformIcon";
import {
  Avatar,
  Button,
  Card,
  Divider,
  Flex,
  Form,
  Input,
  List,
  Modal,
  Select,
  Space,
} from "antd";
import { useMemo, useState } from "react";
import { useDebouncedCallback } from "use-debounce";
import styles from "./InfluencerForm.module.scss";

const { Option } = Select;
interface Props {
  initialFormData: IInfluencerRequestAdd | undefined;
  onApprove: (
    id: string | undefined,
    updatedDetails: ICreateInfluencerRequest,
    remarks: string | undefined
  ) => void;
  onReject: (id: string | undefined, remarks: string | undefined) => void;
  isApproving: boolean;
  isRejecting: boolean;
}

export const InfluencerForm = ({
  initialFormData,
  onApprove,
  isApproving,
  isRejecting,
  onReject,
}: Props) => {
  const [form] = Form.useForm();
  const [newRole, setNewRole] = useState("");
  const selectedInfluencers = Form.useWatch<Partial<SocialMedia>[]>(
    ["socialMedias"],
    form
  );

  const { facets } = useGetInfluencerFacets();

  const [selectedLocation, setSelectedLocation] = useState<
    | {
        label: string;
        value: string;
      }
    | undefined
  >(
    initialFormData
      ? {
          label: initialFormData.locationObj?.description || "",
          value: initialFormData.locationObj?.locationId || "",
        }
      : undefined
  );

  const [searchLocation, setSearchLocation] = useState<string>("");

  const { locationFacets, isFetching } = useGetLocationFacets(searchLocation);

  const availableCategories = useMemo(() => {
    return facets?.category?.map((val) => val._id);
  }, [facets]);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [addSocialMedia, setAddSocialMedia] = useState<boolean>(false);

  const disableFormField = initialFormData?.status !== "Pending";

  const handleAdd = (values: Partial<SocialMedia>) => {
    const influencers = [...selectedInfluencers];

    form.setFieldValue("socialMedias", [...influencers, values]);
    setAddSocialMedia(false);
  };

  const deleteSocialMedia = (item: Partial<SocialMedia>) => {
    const influencers = [...selectedInfluencers];
    form.setFieldValue(
      "socialMedias",
      influencers.filter(
        (val: Partial<SocialMedia>) => val.username !== item.username
      )
    );
  };

  const handleSelectLocation = (value: string) => {
    const selected = (locationFacets || []).find(
      (item) => item.locationId === value
    );
    if (selected) {
      setSelectedLocation({
        label: selected.description,
        value: selected.locationId,
      });
      form.setFieldValue("locationId", value);
    }
  };

  const debouncedSearchLocation = useDebouncedCallback((val: string) => {
    setSearchLocation(val);
  }, 250);

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          ...initialFormData,
          countryCode: "+91",
          locationId: initialFormData?.locationObj?.locationId,
          socialMedias: initialFormData?.socialMedias,
          agencyUserId: initialFormData?.agencyUser?.name,
        }}
      >
        <div className={styles.form}>
          <Form.Item name="firstName" label="First Name">
            <Input placeholder="First Name" disabled={disableFormField} />
          </Form.Item>

          <Form.Item name="lastName" label="Last Name">
            <Input placeholder="Last Name" disabled={disableFormField} />
          </Form.Item>

          <Form.Item label="Location" name={"locationId"}>
            <Select
              showSearch
              onSearch={debouncedSearchLocation}
              placeholder="Search Location"
              value={selectedLocation?.value || undefined} // Ensure value is a string or undefined
              filterOption={(input, option) => true}
              disabled={disableFormField}
            >
              {/* Show selected option at the top */}
              {selectedLocation && (
                <Select.Option
                  key={selectedLocation.value}
                  value={selectedLocation.value}
                >
                  {selectedLocation.label}
                </Select.Option>
              )}

              {/* Render location options */}
              {(locationFacets || []).length > 0
                ? (locationFacets || [])
                    .filter(
                      (item) => item.locationId !== selectedLocation?.value
                    )
                    .map((location) => (
                      <Select.Option
                        key={location.locationId}
                        value={location.locationId}
                      >
                        {location.description}
                      </Select.Option>
                    ))
                : !isFetching &&
                  !selectedLocation && (
                    <Select.Option disabled key="no-data" value="no-data">
                      No data available
                    </Select.Option>
                  )}
            </Select>
          </Form.Item>

          <Form.Item name="category" label="Category">
            <Select
              open={dropdownOpen}
              onDropdownVisibleChange={setDropdownOpen}
              showSearch
              mode="multiple"
              placeholder="Select or create a role"
              optionFilterProp="children"
              allowClear
              disabled={disableFormField}
              dropdownRender={(menu) => (
                <>
                  {menu}
                  <Divider style={{ margin: "8px 0" }} />
                  <Space
                    style={{
                      padding: "0 8px 4px",
                      width: "100%",
                      justifyContent: "space-between",
                    }}
                  >
                    <Input
                      placeholder="Enter new category"
                      value={newRole}
                      onChange={(e) => setNewRole(e.target.value)}
                    />
                    <Button
                      type="text"
                      icon={<PlusOutlined />}
                      disabled={!newRole}
                    >
                      Add Category
                    </Button>
                  </Space>
                </>
              )}
              filterOption={(input, option) =>
                (option?.children as unknown as string)
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              {availableCategories?.map((role) => (
                <Option key={role} value={role}>
                  {role}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="gender" label="Gender">
            <Select disabled={disableFormField}>
              <Option value={GenderEnum.male}>{GenderEnum.male}</Option>
              <Option value={GenderEnum.female}>{GenderEnum.female}</Option>
              <Option value={GenderEnum.others}>{GenderEnum.others}</Option>
            </Select>
          </Form.Item>
          <Form.Item label="Talent Manager" name="agencyUserId">
            <Select disabled></Select>
          </Form.Item>
          <Form.Item name="socialMedias">
            <Card
              title={
                <Flex justify="space-between">
                  <span>Social Media</span>
                  {initialFormData?.status !== "Rejected" && (
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => setAddSocialMedia(true)}
                    >
                      Add Social Media
                    </Button>
                  )}
                </Flex>
              }
              styles={{
                body: {
                  padding: "10px",
                },
              }}
            >
              <List
                dataSource={selectedInfluencers || []}
                renderItem={(item) => (
                  <List.Item
                    key={item.username}
                    actions={[
                      initialFormData?.status !== "Rejected" ? (
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => deleteSocialMedia(item)}
                          type="link"
                        />
                      ) : (
                        <></>
                      ),
                    ]}
                  >
                    <List.Item.Meta
                      style={{ alignItems: "center" }}
                      description={
                        <Space>
                          {item.platform && getPlatformIcon(item.platform)}
                          <Button
                            type="link"
                            style={{ padding: "0" }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (item.link) {
                                window.open(item.link, "_blank");
                              }
                            }}
                          >
                            {item.username}
                          </Button>
                          <span>
                            {" "}
                            (
                            {formatData(
                              item.followers
                                ? item.followers
                                : item.searchV1
                                ? item.searchV1.followerCount
                                : 0,
                              "a"
                            )}{" "}
                            followers)
                          </span>
                        </Space>
                      }
                      avatar={<Avatar src={item.profilePicUrl} />}
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Form.Item>
        </div>
        <Divider />
        <Form.Item name="remarks" label="Admin Remarks">
          <Input.TextArea
            placeholder="Enter remarks"
            rows={4}
            disabled={initialFormData?.status !== "Pending"}
          />
        </Form.Item>
        {initialFormData?.status === "Pending" && (
          <div className={styles.footer}>
            <Button
              htmlType="submit"
              loading={isRejecting}
              disabled={isApproving || isRejecting}
              onClick={() =>
                onReject(initialFormData?._id, form.getFieldValue("remarks"))
              }
              style={{ marginRight: "10px" }}
            >
              Reject
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={isApproving}
              disabled={isApproving || isRejecting}
              onClick={() => {
                const finalData = form.getFieldsValue();
                finalData.agencyUserId = initialFormData?.agencyUser?._id;
                onApprove(
                  initialFormData?._id,
                  {
                    ...finalData,
                  },
                  form.getFieldValue("remarks")
                );
              }}
            >
              Approve
            </Button>
          </div>
        )}
      </Form>
      <Modal
        open={addSocialMedia}
        onCancel={() => setAddSocialMedia(false)}
        footer={null}
        destroyOnClose
      >
        <SocialMediaForm
          handleAdd={handleAdd}
          isAdding={false}
          socialMediasToDisplay={(facets?.socialMediaPlatforms || [])?.map(
            (item) => item._id
          )}
        />
      </Modal>
    </>
  );
};

export default InfluencerForm;
